/**
 * ToolsLibraryDialog
 *
 * A dialog for browsing and managing tools
 *
 * Primary responsibilities:
 * - Display available chat tools and custom integrations
 * - Allow users to add/remove tools from quick actions
 * - Show which tools are currently in quick actions
 * - Provide search and filtering capabilities
 */

import { FC, useState, useEffect, useContext, useMemo, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator
} from '@/components/ui/dropdown-menu'
import {
  IconDots,
  IconApps,
  IconSearch,
  IconCheck,
  IconPlus,
  IconMinus,
  IconEdit,
  IconTrash,
  IconCopy,
  IconChevronLeft,
  IconSparkle2,
  IconServer,
  IconCertificate
} from '@tabler/icons-react'
import { ChatbotUIContext } from '@/context/context'
import { ChatbotUIChatContext } from '@/context/chat'
import { cn } from '@/lib/utils'
import { deleteTool } from '@/db/tools'
import { Tables } from '@/types/supabase'
import { getToolIcons } from '@/lib/toolIconMapping'
import { McpSetupCardView, type McpSetupCard } from '@/components/integrations/mcp-setup-card'
import { SkillSetupCardView, type SkillSetupCard } from '@/components/integrations/skill-setup-card'
import { CatalogInstallSearch } from '@/components/integrations/catalog-install-search'

// Define a proper tool type that matches the actual data structure
type ToolData = Tables<'tools'> & {
  category?: string
  parameters?: (parameters: unknown) => unknown
}

type InstalledMcpIntegration = {
  auth_type: string
  enabled_surfaces: readonly string[]
  id: string
  last_error: string | null
  name: string
  server_url: string | null
  status: string
  transport: string
}

type InstalledSkillIntegration = {
  description: string | null
  display_name: string
  enabled_surfaces: readonly string[]
  id: string
  last_error: string | null
  name: string
  repository: string | null
  skill_path: string | null
  source_type: string
  source_url: string | null
  status: string
}

function isCustomIntegrationTool(tool: ToolData) {
  return Boolean(tool.user_id && tool.sharing !== 'platform')
}

function canManageCustomIntegration(tool: ToolData, profileUserId?: string | null) {
  return isCustomIntegrationTool(tool) && Boolean(profileUserId && tool.user_id === profileUserId)
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((item) => typeof item === 'string')
}

function isInstalledMcpIntegration(value: unknown): value is InstalledMcpIntegration {
  if (!isRecord(value)) return false

  return (
    typeof value.id === 'string' &&
    typeof value.name === 'string' &&
    typeof value.status === 'string' &&
    typeof value.transport === 'string' &&
    typeof value.auth_type === 'string' &&
    isStringArray(value.enabled_surfaces)
  )
}

function isInstalledSkillIntegration(value: unknown): value is InstalledSkillIntegration {
  if (!isRecord(value)) return false

  return (
    typeof value.id === 'string' &&
    typeof value.name === 'string' &&
    typeof value.display_name === 'string' &&
    typeof value.status === 'string' &&
    typeof value.source_type === 'string' &&
    isStringArray(value.enabled_surfaces)
  )
}

function getInstalledMcpIntegrations(value: unknown): InstalledMcpIntegration[] {
  if (!isRecord(value) || !isRecord(value.installed) || !Array.isArray(value.installed.mcp)) {
    return []
  }

  return value.installed.mcp.filter(isInstalledMcpIntegration)
}

function getInstalledSkillIntegrations(value: unknown): InstalledSkillIntegration[] {
  if (!isRecord(value) || !isRecord(value.installed) || !Array.isArray(value.installed.skills)) {
    return []
  }

  return value.installed.skills.filter(isInstalledSkillIntegration)
}

function getResponseMessage(value: unknown, fallback: string): string {
  if (!isRecord(value)) return fallback

  const message = value.message ?? value.error
  return typeof message === 'string' && message.trim() ? message : fallback
}

function buildEmptyOpenApiSchema(title: string, description: string) {
  return {
    openapi: '3.0.0',
    info: {
      title,
      version: '1.0.0',
      description
    },
    servers: [
      {
        url: 'https://losi.local/installed-integration-context'
      }
    ],
    paths: {}
  }
}

function buildSyntheticTool(
  input: {
    category: string
    description: string
    id: string
    name: string
  },
  userId?: string | null
): ToolData {
  return {
    can_be_used_in_applications: false,
    category: input.category,
    created_at: new Date(0).toISOString(),
    custom_headers: {},
    description: input.description,
    folder_id: null,
    id: input.id,
    name: input.name,
    project_id: null,
    schema: buildEmptyOpenApiSchema(input.name, input.description),
    sharing: 'platform',
    updated_at: null,
    url: 'https://losi.local/installed-integration-context',
    user_id: userId ?? ''
  } as ToolData
}

function buildMcpTool(integration: InstalledMcpIntegration, userId?: string | null): ToolData {
  return buildSyntheticTool(
    {
      category: 'MCP servers',
      description:
        `Installed MCP server: ${integration.name}. Use ` +
        `MCPIntegrationTool__listMcpServerTools and ` +
        `MCPIntegrationTool__callMcpServerTool with integrationId "${integration.id}".`,
      id: `mcp-integration:${integration.id}`,
      name: `MCP: ${integration.name}`
    },
    userId
  )
}

function getSkillSourceLabel(skill: InstalledSkillIntegration): string {
  if (skill.repository) {
    return [skill.repository, skill.skill_path].filter(Boolean).join('/')
  }

  return skill.source_url ?? skill.source_type
}

function buildSkillTool(integration: InstalledSkillIntegration, userId?: string | null): ToolData {
  return buildSyntheticTool(
    {
      category: 'Skills',
      description:
        `Installed Skill: ${integration.display_name}. Use ` +
        `SkillIntegrationTool__runSkill or ` +
        `SkillIntegrationTool__getSkillInstructions with skillId "${integration.id}". ` +
        `Source: ${getSkillSourceLabel(integration)}.`,
      id: `skill-integration:${integration.id}`,
      name: `Skill: ${integration.display_name}`
    },
    userId
  )
}

function isInstalledIntegrationTool(tool: ToolData) {
  return tool.id.startsWith('mcp-integration:') || tool.id.startsWith('skill-integration:')
}

import { Badge } from '@/components/ui/badge'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { useQuickActions } from '@/hooks/useQuickActions'
import { CreateToolForm } from './create-tool-form'

interface ToolsLibraryDialogProps {
  isOpen: boolean
  onClose: () => void
  onQuickActionsChange?: () => void
}

export const ToolsLibraryDialog: FC<ToolsLibraryDialogProps> = ({
  isOpen,
  onClose,
  onQuickActionsChange
}) => {
  const { t } = useTranslation()
  const { profile, selectedWorkspace, setTools, tools } = useContext(ChatbotUIContext)
  const { selectedTools, setSelectedTools } = useContext(ChatbotUIChatContext)
  const { quickActions, addQuickAction, removeQuickAction, isToolInQuickActions } =
    useQuickActions()

  const [searchQuery, setSearchQuery] = useState('')
  const [isProcessing, setIsProcessing] = useState<string | null>(null)
  const [view, setView] = useState<'list' | 'create' | 'edit' | 'mcp' | 'skill'>('list')
  const [editingTool, setEditingTool] = useState<ToolData | null>(null)
  const [installedMcpIntegrations, setInstalledMcpIntegrations] = useState<
    InstalledMcpIntegration[]
  >([])
  const [installedSkillIntegrations, setInstalledSkillIntegrations] = useState<
    InstalledSkillIntegration[]
  >([])
  const [isLoadingInstalledIntegrations, setIsLoadingInstalledIntegrations] = useState(false)
  const [mcpSetup, setMcpSetup] = useState<McpSetupCard | null>(null)
  const [skillSetup, setSkillSetup] = useState<SkillSetupCard | null>(null)

  const loadInstalledIntegrations = useCallback(async () => {
    if (!selectedWorkspace?.id) {
      setInstalledMcpIntegrations([])
      setInstalledSkillIntegrations([])
      return
    }

    setIsLoadingInstalledIntegrations(true)

    try {
      const params = new URLSearchParams({
        workspace_id: selectedWorkspace.id,
        _: String(Date.now())
      })
      const response = await fetch(`/api/integrations?${params.toString()}`, {
        cache: 'no-store'
      })
      const data: unknown = await response.json()

      if (!response.ok) {
        throw new Error(getResponseMessage(data, 'Failed to load installed integrations'))
      }

      setInstalledMcpIntegrations(getInstalledMcpIntegrations(data))
      setInstalledSkillIntegrations(getInstalledSkillIntegrations(data))
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to load installed integrations')
    } finally {
      setIsLoadingInstalledIntegrations(false)
    }
  }, [selectedWorkspace?.id])

  const installedIntegrationTools = useMemo(
    () => [
      ...installedMcpIntegrations.map((integration) => buildMcpTool(integration, profile?.user_id)),
      ...installedSkillIntegrations.map((integration) =>
        buildSkillTool(integration, profile?.user_id)
      )
    ],
    [installedMcpIntegrations, installedSkillIntegrations, profile?.user_id]
  )

  const libraryTools = useMemo(() => {
    const seenIds = new Set<string>()
    const merged: ToolData[] = []

    for (const tool of [...(tools ?? []), ...installedIntegrationTools]) {
      if (!tool?.id || seenIds.has(tool.id)) continue
      seenIds.add(tool.id)
      merged.push(tool as ToolData)
    }

    return merged
  }, [installedIntegrationTools, tools])

  // Filter and search tools
  const filteredTools = useMemo(() => {
    if (!libraryTools) return []

    return libraryTools.filter((tool) => {
      if (!tool.name) return false

      const matchesSearch =
        searchQuery === '' ||
        tool.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        tool.description?.toLowerCase().includes(searchQuery.toLowerCase())

      return matchesSearch
    })
  }, [libraryTools, searchQuery])

  // Group tools by category if they have one
  const groupedTools = useMemo(() => {
    const groups: { [key: string]: ToolData[] } = {}

    filteredTools.forEach((tool) => {
      // Use a category from the tool if available, otherwise 'General'
      const category = tool.category || 'General'
      if (!groups[category]) {
        groups[category] = []
      }
      groups[category].push(tool)
    })

    return groups
  }, [filteredTools])

  const handleToggleTool = (tool: ToolData) => {
    if (!tool.id) return

    setIsProcessing(tool.id)

    try {
      const isSelected = selectedTools.some((selectedTool) => selectedTool.id === tool.id)

      if (isSelected) {
        setSelectedTools(selectedTools.filter((t) => t.id !== tool.id))
        toast.success(t('Tool disabled'))
      } else {
        setSelectedTools([...selectedTools, tool])
        toast.success(t('Tool enabled'))
      }
    } catch (error) {
      console.error('Error toggling tool:', error)
      toast.error(t('Failed to toggle tool'))
    } finally {
      setIsProcessing(null)
    }
  }

  const handleToggleQuickAction = async (tool: ToolData) => {
    if (!tool.id) return
    if (isInstalledIntegrationTool(tool)) return

    setIsProcessing(tool.id)

    try {
      const isInQuickActions = isToolInQuickActions(tool.id)

      if (isInQuickActions) {
        await removeQuickAction(tool.id)
      } else {
        await addQuickAction(tool.id)
      }

      // Notify parent component to refresh
      onQuickActionsChange?.()
    } catch (error) {
      console.error('Error toggling quick action:', error)
    } finally {
      setIsProcessing(null)
    }
  }

  const resetDialog = () => {
    setSearchQuery('')
    setIsProcessing(null)
    setView('list')
    setEditingTool(null)
    setMcpSetup(null)
    setSkillSetup(null)
  }

  const handleEditTool = (tool: ToolData) => {
    if (!canManageCustomIntegration(tool, profile?.user_id)) {
      toast.error(t('Only the creator can edit this integration'))
      return
    }

    setEditingTool(tool)
    setView('edit')
  }

  const handleDeleteTool = async (tool: ToolData) => {
    if (!tool.id) return

    if (!canManageCustomIntegration(tool, profile?.user_id)) {
      toast.error(t('Only the creator can delete this integration'))
      return
    }

    const confirmed = window.confirm(
      t('Delete this custom integration? Anyone using it will lose access.')
    )

    if (!confirmed) return

    setIsProcessing(tool.id)

    try {
      await deleteTool(tool.id)
      setTools((current) => current.filter((item) => item.id !== tool.id))
      setSelectedTools(selectedTools.filter((item) => item.id !== tool.id))
      toast.success(t('Integration deleted'))
    } catch (error) {
      console.error('Error deleting custom integration:', error)
      toast.error(t('Failed to delete integration'))
    } finally {
      setIsProcessing(null)
    }
  }

  const handleBack = () => {
    setView('list')
    setEditingTool(null)
  }

  useEffect(() => {
    if (!isOpen) {
      resetDialog()
    }
  }, [isOpen])

  useEffect(() => {
    if (isOpen) {
      void loadInstalledIntegrations()
    }
  }, [isOpen, loadInstalledIntegrations])

  useEffect(() => {
    const handleRefresh = () => {
      void loadInstalledIntegrations()
    }

    window.addEventListener('losi-integrations-refresh', handleRefresh)

    return () => {
      window.removeEventListener('losi-integrations-refresh', handleRefresh)
    }
  }, [loadInstalledIntegrations])

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="flex h-[80vh] max-w-4xl flex-col overflow-hidden">
          <DialogHeader>
            <div className="flex items-center gap-3">
              {view !== 'list' && (
                <Button variant="ghost" size="icon" onClick={handleBack} className="shrink-0">
                  <IconChevronLeft size={20} />
                </Button>
              )}
              <div className="flex flex-1 items-center justify-between">
                <div>
                  <DialogTitle className="flex items-center gap-2">
                    <IconApps size={24} />
                    {view === 'list' && t('Tools Library')}
                    {view === 'create' && t('Create Custom Integration')}
                    {view === 'edit' && t('Edit Custom Integration')}
                    {view === 'mcp' && t('Add MCP Server')}
                    {view === 'skill' && t('Install Skill')}
                  </DialogTitle>
                  <DialogDescription>
                    {view === 'list' &&
                      t('Browse available tools and custom integrations for AI use')}
                    {view === 'create' && t('Create a new custom integration')}
                    {view === 'edit' && t('Edit custom integration configuration')}
                    {view === 'mcp' && t('Search or paste a hosted MCP server URL')}
                    {view === 'skill' && t('Search skills marketplace or paste GitHub skill URL')}
                  </DialogDescription>
                </div>
                {view === 'list' && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button className="flex items-center gap-2">
                        <IconPlus size={16} />
                        {t('Add')}
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-52">
                      <DropdownMenuItem onClick={() => setView('create')}>
                        <IconSparkle2 size={16} className="mr-2" />
                        {t('Custom integration')}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setView('mcp')}>
                        <IconServer size={16} className="mr-2" />
                        {t('MCP server')}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setView('skill')}>
                        <IconCertificate size={16} className="mr-2" />
                        {t('Skill')}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>
            </div>
          </DialogHeader>

          <div className="relative flex min-h-0 flex-1 flex-col space-y-4 overflow-hidden">
            <AnimatePresence mode="wait">
              {view === 'list' && (
                <motion.div
                  key="list"
                  initial={{ x: 0, opacity: 1 }}
                  animate={{ x: 0, opacity: 1 }}
                  exit={{ x: '-100%', opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="flex min-h-0 flex-1 flex-col space-y-4"
                >
                  {/* Search */}
                  <div className="relative">
                    <IconSearch
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                      size={16}
                    />
                    <Input
                      placeholder={t('Search tools and integrations...')}
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10"
                    />
                  </div>

                  {/* Enabled Tools Summary */}
                  <div className="rounded-lg bg-muted/30 p-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <IconCheck size={16} className="text-green-600" />
                        <span className="text-sm font-medium">
                          {t('Enabled Tools')}: {selectedTools.length}
                        </span>
                      </div>
                      <span className="text-xs text-muted-foreground">
                        {t('Tools available to Losi')}
                      </span>
                    </div>
                  </div>

                  {/* Tools Grid */}
                  <div className="min-h-0 flex-1 space-y-6 overflow-y-auto">
                    {Object.entries(groupedTools).map(([category, categoryTools]) => (
                      <div key={category}>
                        <h3 className="mb-3 flex items-center gap-2 text-lg font-semibold">
                          {category}
                          <Badge variant="outline" className="text-xs">
                            {categoryTools.length}
                          </Badge>
                        </h3>

                        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                          {categoryTools.map((tool) => {
                            const isEnabled = selectedTools.some(
                              (selectedTool) => selectedTool.id === tool.id
                            )
                            const isLoading = isProcessing === tool.id
                            const canManageTool = canManageCustomIntegration(tool, profile?.user_id)
                            const iconConfig = getToolIcons(tool.id)
                            const toolIcon = tool.id.startsWith('mcp-integration:') ? (
                              <IconServer size={20} className="text-muted-foreground" />
                            ) : tool.id.startsWith('skill-integration:') ? (
                              <IconCertificate size={20} className="text-muted-foreground" />
                            ) : iconConfig ? (
                              isEnabled ? (
                                iconConfig.enabledIcon
                              ) : (
                                iconConfig.disabledIcon
                              )
                            ) : (
                              <IconApps size={20} className="text-muted-foreground" />
                            )

                            return (
                              <div
                                key={tool.id}
                                className={cn(
                                  'rounded-lg border p-4 transition-all hover:border-primary/50',
                                  isEnabled && 'border-primary bg-primary/5'
                                )}
                              >
                                <div className="flex items-start justify-between">
                                  <div className="flex min-w-0 flex-1 items-start gap-3">
                                    <div className="mt-1 shrink-0">{toolIcon}</div>

                                    <div className="min-w-0 flex-1">
                                      <div className="mb-1 flex items-center gap-2">
                                        <h4 className="truncate font-medium">{tool.name}</h4>
                                        {isEnabled && (
                                          <Badge variant="default" className="text-xs">
                                            {t('Enabled')}
                                          </Badge>
                                        )}
                                      </div>

                                      <p className="line-clamp-2 text-sm text-muted-foreground">
                                        {tool.description || t('No description available')}
                                      </p>
                                    </div>
                                  </div>

                                  <div className="ml-2 flex shrink-0 items-center gap-2">
                                    <div className="shrink-0">
                                      <Switch
                                        checked={isEnabled}
                                        onCheckedChange={() => handleToggleTool(tool)}
                                        disabled={isLoading}
                                      />
                                    </div>
                                    <DropdownMenu>
                                      <DropdownMenuTrigger asChild>
                                        <Button variant="ghost" size="sm" className="size-8 p-0">
                                          <IconDots size={16} />
                                        </Button>
                                      </DropdownMenuTrigger>
                                      <DropdownMenuContent align="end">
                                        {/* Add/Remove from Quick Actions - Show for all tools */}
                                        {tool.id && !isInstalledIntegrationTool(tool) && (
                                          <>
                                            <DropdownMenuItem
                                              onClick={() => handleToggleQuickAction(tool)}
                                            >
                                              {isToolInQuickActions(tool.id) ? (
                                                <>
                                                  <IconMinus size={16} className="mr-2" />
                                                  {t('Remove from Quick Actions')}
                                                </>
                                              ) : (
                                                <>
                                                  <IconPlus size={16} className="mr-2" />
                                                  {t('Add to Quick Actions')}
                                                </>
                                              )}
                                            </DropdownMenuItem>
                                            <DropdownMenuSeparator />
                                          </>
                                        )}

                                        {/* Only show edit/delete for user-created integrations */}
                                        {isCustomIntegrationTool(tool) && (
                                          <>
                                            <DropdownMenuSeparator />
                                            <DropdownMenuItem
                                              onClick={() => handleEditTool(tool)}
                                              disabled={!canManageTool}
                                            >
                                              <IconEdit size={16} className="mr-2" />
                                              {t('Edit custom integration')}
                                            </DropdownMenuItem>
                                            <DropdownMenuItem>
                                              <IconCopy size={16} className="mr-2" />
                                              {t('Duplicate custom integration')}
                                            </DropdownMenuItem>
                                            <DropdownMenuSeparator />
                                            <DropdownMenuItem
                                              className="text-destructive"
                                              onClick={() => handleDeleteTool(tool)}
                                              disabled={!canManageTool || isLoading}
                                            >
                                              <IconTrash size={16} className="mr-2" />
                                              {t('Delete custom integration')}
                                            </DropdownMenuItem>
                                          </>
                                        )}

                                        {/* Show duplicate for platform tools */}
                                        {(!tool.user_id || tool.sharing === 'platform') &&
                                          !isInstalledIntegrationTool(tool) && (
                                            <>
                                              <DropdownMenuSeparator />
                                              <DropdownMenuItem>
                                                <IconCopy size={16} className="mr-2" />
                                                {t('Duplicate Tool')}
                                              </DropdownMenuItem>
                                            </>
                                          )}

                                        {isInstalledIntegrationTool(tool) && (
                                          <DropdownMenuItem disabled>
                                            {tool.id.startsWith('mcp-integration:')
                                              ? t('Installed MCP server')
                                              : t('Installed skill')}
                                          </DropdownMenuItem>
                                        )}
                                      </DropdownMenuContent>
                                    </DropdownMenu>
                                  </div>
                                </div>
                              </div>
                            )
                          })}
                        </div>
                      </div>
                    ))}

                    {isLoadingInstalledIntegrations && filteredTools.length === 0 && (
                      <div className="py-8 text-center text-muted-foreground">
                        <IconApps size={48} className="mx-auto mb-4 opacity-50" />
                        <p>{t('Loading installed integrations...')}</p>
                      </div>
                    )}

                    {!isLoadingInstalledIntegrations && filteredTools.length === 0 && (
                      <div className="py-8 text-center text-muted-foreground">
                        <IconApps size={48} className="mx-auto mb-4 opacity-50" />
                        <p>{t('No tools or integrations found matching your search')}</p>
                      </div>
                    )}
                  </div>
                </motion.div>
              )}

              {view === 'create' && (
                <CreateToolForm
                  onSuccess={() => setView('list')}
                  onCancel={() => setView('list')}
                />
              )}

              {view === 'edit' && editingTool && (
                <CreateToolForm
                  existingTool={editingTool}
                  onSuccess={handleBack}
                  onCancel={handleBack}
                />
              )}

              {view === 'mcp' && (
                <motion.div
                  key="mcp"
                  initial={{ x: '100%', opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  exit={{ x: '100%', opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="flex min-h-0 flex-1 flex-col space-y-4"
                >
                  <CatalogInstallSearch
                    initialQuery={searchQuery}
                    kind="mcp"
                    onInstalled={loadInstalledIntegrations}
                    onMcpSetup={setMcpSetup}
                    workspaceId={selectedWorkspace?.id}
                  />

                  {mcpSetup ? <McpSetupCardView compact setup={mcpSetup} /> : null}
                </motion.div>
              )}

              {view === 'skill' && (
                <motion.div
                  key="skill"
                  initial={{ x: '100%', opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  exit={{ x: '100%', opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="flex min-h-0 flex-1 flex-col space-y-4"
                >
                  <CatalogInstallSearch
                    initialQuery={searchQuery}
                    kind="skills"
                    onInstalled={loadInstalledIntegrations}
                    onSkillSetup={setSkillSetup}
                    workspaceId={selectedWorkspace?.id}
                  />

                  {skillSetup ? <SkillSetupCardView compact setup={skillSetup} /> : null}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}
